// Transcodes the KTX2 (Basis Universal) atlases of hlod-tileset node payloads to a GPU format.
// Shared by the main-thread pool (sources/hlod) and the worker handler (workers/hlod-transcode).
// The transcoder files come from the host (the demo vendors them in src/assets/decoders/basis/);
// it runs in a worker so the main thread only uploads the finished mip levels.

/** GPU formats a Basis texture is transcoded to; `rgba8` is the uncompressed fallback. */
export type HsHlodTextureTarget = 'bc1' | 'etc1' | 'bc7' | 'astc' | 'rgba8';

export interface HsHlodTextureTargets {
  /** Target for opaque ETC1S textures, the converter's codec. */
  etc1s: HsHlodTextureTarget;
  /** Target for UASTC textures or textures with alpha. */
  uastc: HsHlodTextureTarget;
  /** WebGL internal format per available target; RGBA8 uploads with texImage2D instead. */
  internalFormats: Partial<Record<HsHlodTextureTarget, number>>;
}

export interface HsHlodTranscodedLevel {
  width: number;
  height: number;
  data: Uint8Array;
}

export interface HsHlodTranscodedTexture {
  target: HsHlodTextureTarget;
  width: number;
  height: number;
  levels: HsHlodTranscodedLevel[];
  /** GPU bytes of all levels, which the tile cache budget counts. */
  bytes: number;
}

/** The parts of the transcoder module's embind API this file uses. */
export interface HsBasisModule {
  initializeBasis(): void;
  KTX2File: new (data: Uint8Array) => BasisKtx2File;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- the transcoder's embind name
  transcoder_texture_format: Record<string, { value: number }>;
}

interface BasisKtx2File {
  isValid(): boolean;
  getWidth(): number;
  getHeight(): number;
  getLevels(): number;
  getHasAlpha(): boolean;
  isUASTC(): boolean;
  startTranscoding(): boolean;
  getImageLevelInfo(level: number, layer: number, face: number): { origWidth: number; origHeight: number };
  getImageTranscodedSizeInBytes(level: number, layer: number, face: number, format: number): number;
  transcodeImage(
    target: Uint8Array,
    level: number,
    layer: number,
    face: number,
    format: number,
    flags: number,
    channel0: number,
    channel1: number
  ): number;
  close(): void;
  delete(): void;
}

const BASIS_FORMATS: Record<HsHlodTextureTarget, string> = {
  bc1: 'cTFBC1_RGB',
  etc1: 'cTFETC1_RGB',
  bc7: 'cTFBC7_RGBA',
  astc: 'cTFASTC_4x4_RGBA',
  rgba8: 'cTFRGBA32',
};

// ETC1S is a subset of ETC1, so ETC1 keeps it exactly; BC1 comes first anyway, because desktop GPUs
// support it natively everywhere while ETC may be emulated there. Both take 4 bits per texel; BC7 and
// ASTC 4x4 take 8, RGBA8 32.
const ETC1S_PRIORITY: HsHlodTextureTarget[] = ['bc1', 'etc1', 'bc7', 'astc', 'rgba8'];
// UASTC keeps more detail, which only the 8-bit-per-texel formats preserve.
const UASTC_PRIORITY: HsHlodTextureTarget[] = ['astc', 'bc7', 'rgba8'];

/** Picks transcode targets from the context's compressed texture extensions. */
export function hsSelectHlodTextureTargets(gl: WebGL2RenderingContext): HsHlodTextureTargets {
  const internalFormats: Partial<Record<HsHlodTextureTarget, number>> = {};
  const s3tc: { COMPRESSED_RGB_S3TC_DXT1_EXT: number } | null = gl.getExtension('WEBGL_compressed_texture_s3tc');
  if (s3tc !== null) {
    internalFormats.bc1 = s3tc.COMPRESSED_RGB_S3TC_DXT1_EXT;
  }
  const etc: { COMPRESSED_RGB8_ETC2: number } | null = gl.getExtension('WEBGL_compressed_texture_etc');
  const etc1: { COMPRESSED_RGB_ETC1_WEBGL: number } | null = gl.getExtension('WEBGL_compressed_texture_etc1');
  if (etc !== null) {
    // ETC2 decoders read ETC1 blocks unchanged.
    internalFormats.etc1 = etc.COMPRESSED_RGB8_ETC2;
  } else if (etc1 !== null) {
    internalFormats.etc1 = etc1.COMPRESSED_RGB_ETC1_WEBGL;
  }
  const bptc: { COMPRESSED_RGBA_BPTC_UNORM_EXT: number } | null = gl.getExtension('EXT_texture_compression_bptc');
  if (bptc !== null) {
    internalFormats.bc7 = bptc.COMPRESSED_RGBA_BPTC_UNORM_EXT;
  }
  // eslint-disable-next-line @typescript-eslint/naming-convention -- the WebGL extension's constant
  const astc: { COMPRESSED_RGBA_ASTC_4x4_KHR: number } | null = gl.getExtension('WEBGL_compressed_texture_astc');
  if (astc !== null) {
    internalFormats.astc = astc.COMPRESSED_RGBA_ASTC_4x4_KHR;
  }
  const available = (target: HsHlodTextureTarget): boolean =>
    target === 'rgba8' || internalFormats[target] !== undefined;
  return {
    etc1s: ETC1S_PRIORITY.find(available) ?? 'rgba8',
    uastc: UASTC_PRIORITY.find(available) ?? 'rgba8',
    internalFormats,
  };
}

export function hsTranscodeKtx2(
  basis: HsBasisModule,
  data: Uint8Array,
  targets: Pick<HsHlodTextureTargets, 'etc1s' | 'uastc'>
): HsHlodTranscodedTexture {
  const file: BasisKtx2File = new basis.KTX2File(data);
  try {
    if (!file.isValid() || !file.startTranscoding()) {
      throw new Error('HLOD node texture is not a valid Basis Universal KTX2 file.');
    }
    const target: HsHlodTextureTarget = file.isUASTC() || file.getHasAlpha() ? targets.uastc : targets.etc1s;
    const format: number = basis.transcoder_texture_format[BASIS_FORMATS[target]].value;
    const levels: HsHlodTranscodedLevel[] = [];
    let bytes: number = 0;
    for (let level: number = 0; level < Math.max(1, file.getLevels()); level++) {
      const { origWidth, origHeight }: { origWidth: number; origHeight: number } = file.getImageLevelInfo(level, 0, 0);
      const levelData: Uint8Array = new Uint8Array(file.getImageTranscodedSizeInBytes(level, 0, 0, format));
      if (file.transcodeImage(levelData, level, 0, 0, format, 0, -1, -1) === 0) {
        throw new Error(`HLOD node texture level ${level} failed to transcode.`);
      }
      levels.push({ width: origWidth, height: origHeight, data: levelData });
      bytes += levelData.byteLength;
    }
    return { target, width: file.getWidth(), height: file.getHeight(), levels, bytes };
  } finally {
    file.close();
    file.delete();
  }
}

export interface HsHlodTranscodeRequest {
  id: number;
  data: ArrayBuffer;
  targets: Pick<HsHlodTextureTargets, 'etc1s' | 'uastc'>;
  /** Absolute URLs of the vendored transcoder script and its WebAssembly binary. */
  scriptUrl: string;
  wasmUrl: string;
}

export type HsHlodTranscodeResponse =
  | { id: number; ok: true; texture: HsHlodTranscodedTexture }
  | { id: number; ok: false; error: string };
